import { Instagram, Youtube, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { ASSETS, SITE } from "@/lib/site";

const LEGAL_LINKS = [
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/plans", label: "Plans & Pricing" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/refund", label: "Refund Policy" },
  { to: "/cancellation", label: "Cancellation Policy" },
  { to: "/shipping", label: "Shipping & Delivery" },
];

export const Footer = () => {
  return (
    <footer className="bg-[#050505] border-t border-white/5 pt-16 pb-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/95 p-1">
                <img src={ASSETS.logo} alt="AU Movies" className="w-full h-full object-contain rounded-full" />
              </div>
              <div>
                <p className="font-serif text-xl text-white">{SITE.name}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#2DD4BF]">Production House</p>
              </div>
            </div>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-sm">
              {SITE.tagline}. Telling stories from South India for Pan-India and Asia — in six
              languages, shot as one.
            </p>
            <div className="mt-6 text-xs text-slate-500 space-y-1">
              <p><span className="text-slate-600">Legal name:</span> {SITE.legalName}</p>
              <p><span className="text-slate-600">CIN:</span> {SITE.cin}</p>
              <p><span className="text-slate-600">GSTIN:</span> {SITE.gstin}</p>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-4">Studio</p>
            <p className="text-slate-300 text-sm leading-relaxed flex gap-2">
              <MapPin size={14} className="text-[#2DD4BF] mt-1 shrink-0" />
              <span>
                {SITE.address.line1}<br />
                {SITE.address.line2}<br />
                {SITE.address.line3} – {SITE.address.pincode}<br />
                {SITE.address.state}, India
              </span>
            </p>
            <a
              href={`tel:${SITE.phone}`}
              className="mt-4 flex items-center gap-2 text-slate-300 hover:text-[#2DD4BF] transition-colors text-sm"
              data-testid="footer-phone"
            >
              <Phone size={14} /> {SITE.phoneDisplay}
            </a>
            <a
              href={`mailto:${SITE.email}`}
              className="mt-2 flex items-center gap-2 text-slate-300 hover:text-[#2DD4BF] transition-colors text-sm"
              data-testid="footer-email"
            >
              <Mail size={14} /> {SITE.email}
            </a>
          </div>

          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-4">Legal & Policies</p>
            <div className="grid grid-cols-2 gap-y-2 gap-x-4">
              {LEGAL_LINKS.map((l) => (
                <a
                  key={l.to}
                  href={l.to}
                  data-testid={`footer-link-${l.to.replace('/', '')}`}
                  className="text-sm text-slate-300 hover:text-[#2DD4BF] transition-colors"
                >
                  {l.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-8">
              <a
                href={SITE.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-300 hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-all"
                data-testid="footer-instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={SITE.socials.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-300 hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-all"
                data-testid="footer-youtube"
              >
                <Youtube size={16} />
              </a>
              <a
                href={SITE.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-300 hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-all"
                data-testid="footer-facebook"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="divider-aum mt-12" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-6 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} {SITE.legalName}. All rights reserved.</p>
          <p>Crafted in Bangalore · Payments secured by PayU · PhonePe (PCI-DSS)</p>
        </div>
      </div>
    </footer>
  );
};
